import type {
  EngineConfig,
  FrameInputs,
  Snapshot,
  EngineEvent,
  PlayerSnapshot,
  EnemySnapshot,
  BulletSnapshot,
  PickupSnapshot,
  ParticleSnapshot,
  BossSnapshot,
  Vec2,
} from './types.js';
import { World } from './world/world.js';
import { EventQueue } from './world/events.js';
import { SpawnDirector } from './world/spawn.js';
import { BossController } from './world/boss.js';
import { updateMovement } from './world/movement.js';
import { updatePlayerWeapons, updateHomingBullets } from './world/weapons.js';
import { updateEnemyAi } from './world/ai.js';
import { updateCollisions } from './world/collision.js';
import { updateLives } from './world/lives.js';
import { applyBomb } from './world/effects.js';
import { currentPickupWeapon } from './world/pickups.js';
import { mulberry32, type Prng } from './prng.js';
import { clamp } from './math.js';

export class Engine {
  private config: EngineConfig;
  private world: World;
  private events: EventQueue;
  private director: SpawnDirector;
  private bossController: BossController;
  private rng: Prng;
  private snapshot: Snapshot;

  constructor(config: EngineConfig) {
    this.config = config;
    this.world = new World(config.worldWidth, config.worldHeight);
    this.events = new EventQueue();
    this.rng = mulberry32(config.seed);
    this.director = new SpawnDirector(config);
    this.bossController = new BossController(config);
    this.initWorld();
    this.snapshot = this.buildSnapshot();
  }

  private initWorld(): void {
    this.world.spawnPlayer(this.config.worldWidth * 0.5, this.config.worldHeight - 100, {
      kind: 'spread',
      level: 1,
      fireCooldownMs: 0,
    });
  }

  tick(dtMs: number, inputs: FrameInputs): void {
    const world = this.world;
    if (world.phase === 'gameover') {
      // Advance time + director even in gameover (enables boss spawn in cinematic
      // mode and keeps timeMs monotonic for determinism tests).
      this.director.update(world, dtMs);
      this.bossController.update(world, dtMs, this.events);
      world.tick += 1;
      world.timeMs += dtMs;
      this.snapshot = this.buildSnapshot();
      return;
    }

    // 1) Drain inputs — map to first alive player.
    const inp = inputs[0];
    if (inp) {
      for (const p of world.players) {
        if (!p.alive) continue;
        if (p.respawnMs > 0) {
          p.vel.x = 0;
          p.vel.y = 0;
          continue;
        }
        let mx = inp.moveX;
        let my = inp.moveY;
        const mag = Math.sqrt(mx * mx + my * my);
        if (mag > 1) {
          mx /= mag;
          my /= mag;
        }
        const speed = 320;
        p.vel.x = mx * speed;
        p.vel.y = my * speed;
        if (inp.bomb) {
          applyBomb(world, this.events, this.rng);
        }
      }
    }

    // 2) Spawn director
    this.director.update(world, dtMs);

    // 3) AI
    updateEnemyAi(world, dtMs, this.config);

    // 4) Movement integration
    updateMovement(world, dtMs, this.config);

    // 5) Player auto-fire
    updatePlayerWeapons(world, dtMs, this.config, this.events);

    // 5b) Homing bullet steering
    updateHomingBullets(world, dtMs);

    // 6) Collisions + damage + score
    updateCollisions(world, dtMs, this.config, this.rng, this.events);

    // 7) Pickups — ages handled in movement; nothing extra here.

    // 8) Boss state machine
    this.bossController.update(world, dtMs, this.events);

    // 9) Lives + respawn (timers decay in movement).
    updateLives(world, dtMs);

    // 10/11) Effect queue / cleanup — already in collision & movement.

    // 12) Advance tick counter and build snapshot.
    world.tick += 1;
    world.timeMs += dtMs;
    this.snapshot = this.buildSnapshot();
  }

  getSnapshot(): Snapshot {
    return this.snapshot;
  }

  drainEvents(): EngineEvent[] {
    return this.events.drain();
  }

  reset(seed?: number): void {
    const prevHi = this.world.hiScore;
    this.world = new World(this.config.worldWidth, this.config.worldHeight);
    this.world.hiScore = prevHi;
    this.events = new EventQueue();
    this.rng = mulberry32(seed ?? this.config.seed);
    this.director = new SpawnDirector(this.config);
    this.bossController = new BossController(this.config);
    this.initWorld();
    this.snapshot = this.buildSnapshot();
  }

  serialize(): unknown {
    const w = this.world;
    return {
      tick: w.tick,
      timeMs: w.timeMs,
      phase: w.phase,
      hiScore: w.hiScore,
      shake: w.shake,
      hitStopMs: w.hitStopMs,
      players: w.players,
      enemies: w.enemies,
      bullets: w.bullets,
      pickups: w.pickups,
      particles: w.particles,
      boss: w.boss,
    };
  }

  deserialize(data: unknown): void {
    const d = data as ReturnType<Engine['serialize']> & Record<string, unknown>;
    const w = this.world;
    w.tick = (d.tick as number) ?? 0;
    w.timeMs = (d.timeMs as number) ?? 0;
    w.phase = (d.phase as World['phase']) ?? 'playing';
    w.hiScore = (d.hiScore as number) ?? 0;
    w.shake = (d.shake as number) ?? 0;
    w.hitStopMs = (d.hitStopMs as number) ?? 0;
    w.players = (d.players as World['players']) ?? [];
    w.enemies = (d.enemies as World['enemies']) ?? [];
    w.bullets = (d.bullets as World['bullets']) ?? [];
    w.pickups = (d.pickups as World['pickups']) ?? [];
    w.particles = (d.particles as World['particles']) ?? [];
    w.boss = (d.boss as World['boss']) ?? null;
    this.snapshot = this.buildSnapshot();
  }

  private buildSnapshot(): Snapshot {
    const w = this.world;

    const players: PlayerSnapshot[] = [];
    for (const p of w.players) {
      players.push({
        id: p.id,
        pos: cloneVec(p.pos),
        vel: cloneVec(p.vel),
        hp: p.hp,
        lives: p.lives,
        invulnMs: p.invulnMs,
        weapon: { kind: p.weapon.kind, level: p.weapon.level },
        bombs: p.bombs,
        score: p.score,
        alive: p.alive,
        shieldHp: p.shieldHp,
      });
    }

    const enemies: EnemySnapshot[] = [];
    for (const e of w.enemies) {
      if (!e.alive) continue;
      enemies.push({
        id: e.id,
        archetype: e.archetype,
        pos: cloneVec(e.pos),
        hp: e.hp,
        flashMs: e.flashMs,
      });
    }

    const bullets: BulletSnapshot[] = [];
    for (const b of w.bullets) {
      if (!b.alive) continue;
      bullets.push({
        id: b.id,
        pos: cloneVec(b.pos),
        vel: cloneVec(b.vel),
        team: b.team,
        kind: b.kind,
      });
    }

    const pickups: PickupSnapshot[] = [];
    for (const pk of w.pickups) {
      if (!pk.alive) continue;
      pickups.push({
        id: pk.id,
        pos: cloneVec(pk.pos),
        weapon: currentPickupWeapon(pk),
      });
    }

    const particles: ParticleSnapshot[] = [];
    for (const pt of w.particles) {
      if (!pt.alive) continue;
      particles.push({
        id: pt.id,
        pos: cloneVec(pt.pos),
        vel: cloneVec(pt.vel),
        ageMs: pt.ageMs,
        lifeMs: pt.lifeMs,
        color: pt.color,
        size: pt.size,
      });
    }

    let boss: BossSnapshot | null = null;
    if (w.boss && w.boss.alive) {
      boss = {
        id: w.boss.id,
        pos: cloneVec(w.boss.pos),
        hp: w.boss.hp,
        hpMax: w.boss.hpMax,
        phase: w.boss.phase,
        flashMs: w.boss.flashMs,
      };
    }

    return {
      tick: w.tick,
      timeMs: w.timeMs,
      players,
      enemies,
      bullets,
      pickups,
      particles,
      boss,
      camera: { shake: clamp(w.shake, 0, 40), hitStopMs: w.hitStopMs },
      hiScore: w.hiScore,
      phase: w.phase,
    };
  }
}

function cloneVec(v: Vec2): Vec2 {
  return { x: v.x, y: v.y };
}
